import { IconButton, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';

import { tApp } from '@frontend/locales';
import {
  formUtils,
  paymentUtils,
  usePortalConfiguration,
  useUser,
} from '@frontend/utils';

import { FormikTextField } from '../formik-text-field.component';
import { CrossIcon } from '../icons';

export interface CustomAmountFieldProps {
  selectedAmountOption: number | undefined;
  onCustomAmountChange: (amount: number) => void;
}

export const CustomAmountField = (props: CustomAmountFieldProps) => {
  const theme = useTheme();
  const { values, resetForm } = useFormikContext<{
    customAmount: number | string;
  }>();
  const {
    user: { locale },
  } = useUser();
  const {
    config: { currency },
  } = usePortalConfiguration();
  const [isFieldActive, setIsFieldActive] = useState(false);

  const isCustomAmountSelected = useMemo(
    () =>
      isFieldActive ||
      (typeof values.customAmount === 'string' && values.customAmount !== '') ||
      (typeof values.customAmount === 'number' && values.customAmount > 0),
    [isFieldActive, values.customAmount],
  );

  useEffect(() => {
    if (
      typeof values.customAmount === 'string' &&
      values.customAmount === '' &&
      props.selectedAmountOption === undefined
    ) {
      props.onCustomAmountChange(0);
      return;
    }

    if (typeof values.customAmount === 'number' && values.customAmount <= 0) {
      props.onCustomAmountChange(0);
      return;
    }

    props.onCustomAmountChange(
      formUtils.currency.toModel(values.customAmount)!,
    );
  }, [values.customAmount]);

  useEffect(() => {
    if (props.selectedAmountOption === undefined) {
      return;
    }

    resetForm(); // Reset custom amount input if an amount option is selected
  }, [props.selectedAmountOption]);

  return (
    <FormikTextField
      name="customAmount"
      type="number"
      label={tApp.topup.customAmount()}
      errorText={tApp.topup.invalidAmount()}
      onActive={setIsFieldActive}
      inputMode="decimal"
      sx={{
        fieldset: {
          borderRadius: `${theme.shape.borderRadius}px`,
          border: isCustomAmountSelected
            ? `2px solid ${theme.palette.primary.main}`
            : undefined,
        },
        label: {
          color: isCustomAmountSelected
            ? `${theme.palette.primary.main}`
            : undefined,
        },
      }}
      startAdornment={
        isCustomAmountSelected &&
        paymentUtils.getCurrencySymbol(locale!, currency!)
      }
      endAdornment={
        values.customAmount !== '' && (
          <IconButton onClick={() => resetForm()}>
            <CrossIcon />
          </IconButton>
        )
      }
    />
  );
};
